// Contexto de autenticación propio sobre @insforge/sdk (el paquete @insforge/react
// está deprecado). Hidrata la sesión al arrancar, expone user/loading y las
// acciones signIn/signUp/signOut, y sincroniza el usuario para los movimientos.
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { insforge } from '../lib/insforge';
import { queryClient } from '../lib/queryClient';
import { setUsuarioActual } from '../lib/session';

export interface AuthUser {
  id: string;
  email?: string;
  name?: string;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthState>({
  user: null,
  loading: true,
  async signIn() { return {}; },
  async signUp() { return {}; },
  async signOut() {},
});

// Normaliza el usuario del SDK a la forma que usa la UI y el campo `usuario`.
// El SDK guarda el nombre en profile.name (profile puede ser null).
function normalizar(u: { id: string; email?: string | null; profile?: { name?: string } | null } | null | undefined): AuthUser | null {
  if (!u?.id) return null;
  return { id: u.id, email: u.email ?? undefined, name: u.profile?.name };
}

function nombreVisible(u: AuthUser | null): string {
  return u?.name || u?.email || '';
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const aplicarUsuario = useCallback((u: AuthUser | null) => {
    setUser(u);
    setUsuarioActual(nombreVisible(u));
  }, []);

  // Hidratación en frío: usa la cookie de refresh httpOnly si existe.
  useEffect(() => {
    let cancelado = false;
    (async () => {
      const { data, error } = await insforge.auth.getCurrentUser();
      if (cancelado) return;
      aplicarUsuario(error ? null : normalizar(data?.user));
      setLoading(false);
    })();
    return () => { cancelado = true; };
  }, [aplicarUsuario]);

  const signIn = useCallback<AuthState['signIn']>(async (email, password) => {
    const { data, error } = await insforge.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    aplicarUsuario(normalizar(data?.user));
    queryClient.invalidateQueries();
    return {};
  }, [aplicarUsuario]);

  const signUp = useCallback<AuthState['signUp']>(async (email, password, name) => {
    const { data, error } = await insforge.auth.signUp({ email, password, name });
    if (error) return { error: error.message };
    // Con verificación de email desactivada, signUp devuelve sesión directa.
    if (data?.user) {
      aplicarUsuario(normalizar(data.user));
      queryClient.invalidateQueries();
    }
    return {};
  }, [aplicarUsuario]);

  const signOut = useCallback<AuthState['signOut']>(async () => {
    await insforge.auth.signOut();
    aplicarUsuario(null);
    queryClient.clear();
  }, [aplicarUsuario]);

  return (
    <Ctx.Provider value={{ user, loading, signIn, signUp, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth(): AuthState {
  return useContext(Ctx);
}
